import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import {
  Database, Plus, Sparkles, Trash2, ExternalLink, ChevronDown, ChevronUp, BookOpen,
} from 'lucide-react';
import {
  customObjectsService,
  CustomObjectType,
  VerticalTemplateSummary,
  TemplateListResponse,
} from '@/services/custom-objects.service';
import toast from 'react-hot-toast';
import clsx from 'clsx';

function prettyIndustry(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CustomObjectTypesList() {
  const nav = useNavigate();
  const [types, setTypes] = useState<CustomObjectType[]>([]);
  const [templateData, setTemplateData] = useState<TemplateListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState<string | null>(null);
  const [showOther, setShowOther] = useState(false);

  const refresh = async () => {
    const [t, tp] = await Promise.all([
      customObjectsService.listTypes(true),
      customObjectsService.listTemplates(),
    ]);
    setTypes(t);
    setTemplateData(tp);
  };

  useEffect(() => {
    refresh().catch(() => undefined).finally(() => setLoading(false));
  }, []);

  const onInstall = async (templateId: string) => {
    setInstalling(templateId);
    try {
      const r = await customObjectsService.installTemplate(templateId);
      if (r.installed.length === 0 && r.skipped.length > 0) {
        toast(`Already installed: ${r.skipped.join(', ')}`);
      } else {
        toast.success(`Installed ${r.installed.length} object types`);
      }
      await refresh();
    } catch { toast.error('Install failed'); } finally { setInstalling(null); }
  };

  const onDelete = async (key: string) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete object type "${key}"? Records will be hidden but not erased.`)) return;
    try {
      await customObjectsService.deleteType(key);
      toast.success('Type deleted');
      await refresh();
    } catch { toast.error('Delete failed'); }
  };

  const installedTemplateIds = new Set(types.map((t) => t.installedFromTemplate).filter(Boolean) as string[]);

  const renderTemplateCard = (tpl: VerticalTemplateSummary) => {
    const installed = installedTemplateIds.has(tpl.id);
    return (
      <div
        key={tpl.id}
        className={clsx(
          'rounded-xl border bg-surface px-4 py-3 transition-colors',
          installed ? 'border-success/30 bg-success/5' : 'border-border hover:border-primary/40',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-semibold text-text-primary flex items-center gap-2">
              {tpl.label}
              {installed && (
                <span className="rounded-full bg-success-soft px-1.5 py-0.5 text-2xs font-medium text-success">
                  installed
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-text-muted">{tpl.description}</div>
          </div>
          <Sparkles size={14} className="shrink-0 text-primary opacity-60" />
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {tpl.types.map((ty) => (
            <span key={ty.key} className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">
              {ty.pluralLabel} · {ty.fieldCount}f
            </span>
          ))}
        </div>
        <div className="mt-3">
          <Button
            size="sm"
            variant={installed ? 'ghost' : 'primary'}
            loading={installing === tpl.id}
            onClick={() => onInstall(tpl.id)}
          >
            {installed ? 'Re-install missing types' : 'Install'}
          </Button>
        </div>
      </div>
    );
  };

  const recommended = templateData?.recommended ?? [];
  const other = templateData?.other ?? [];
  const matchedIndustry = templateData?.industry;

  return (
    <>
      <Topbar
        title="Custom Objects"
        breadcrumb="Data"
        description="Build per-business data — doctors, products, projects, anything."
      />

      <div className="px-7 py-7 w-full space-y-6">
        {/* Existing types */}
        <Card padded>
          <SectionHeader
            title="Your object types"
            subtitle={types.length === 0 ? 'No types yet — install a template below or create one from scratch.' : `${types.length} type${types.length === 1 ? '' : 's'} configured`}
            action={
              <div className="flex items-center gap-2">
                <Link
                  to="/custom-objects/examples"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text-primary hover:border-primary hover:text-primary"
                >
                  <BookOpen size={13} />
                  See examples
                </Link>
                <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => nav('/custom-objects/types/new')}>
                  New type
                </Button>
              </div>
            }
          />

          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
            </div>
          ) : types.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-6 py-10 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
                <Database size={20} />
              </div>
              <p className="text-sm font-semibold text-text-primary">No custom object types yet</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">
                Custom Objects let your AI bot answer questions about your data — your menu, doctors, listings,
                products, anything. Two ways to start:
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    document.querySelector<HTMLElement>('[data-templates]')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  iconLeft={<Database size={14} />}
                >
                  Browse vertical templates
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<Plus size={14} />}
                  onClick={() => nav('/custom-objects/types/new')}
                >
                  Create from scratch
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {types.map((t) => (
                <div
                  key={t.key}
                  className={clsx(
                    'flex items-center gap-3 rounded-xl border bg-surface px-4 py-3 transition-colors hover:bg-surface-alt',
                    t.isActive ? 'border-border' : 'border-border opacity-60',
                  )}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <Database size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm font-semibold text-text-primary">{t.label}</span>
                      <span className="rounded-md bg-surface-alt px-1.5 py-0.5 font-mono text-2xs text-text-muted">{t.key}</span>
                      {t.linkToContact && (
                        <span className="rounded-full bg-info-soft px-1.5 py-0.5 text-2xs text-info">linked to Contact</span>
                      )}
                      {!t.isActive && (
                        <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs text-text-muted">inactive</span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-text-muted">
                      {t.fields.length} field{t.fields.length === 1 ? '' : 's'}
                      {t.description ? ` · ${t.description}` : ''}
                    </div>
                  </div>
                  <Link
                    to={`/custom-objects/${t.key}`}
                    className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt inline-flex items-center gap-1"
                  >
                    Records <ExternalLink size={11} />
                  </Link>
                  <Link
                    to={`/custom-objects/types/${t.key}`}
                    className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => onDelete(t.key)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recommended templates (matched to BusinessProfile.industry) */}
        {loading ? (
          <Card padded>
            <SectionHeader title="Install a vertical template" subtitle="Loading recommended templates…" />
            <div className="grid gap-3 sm:grid-cols-2">
              {[0, 1].map((i) => <div key={i} className="skeleton h-32 rounded-xl" />)}
            </div>
          </Card>
        ) : recommended.length > 0 ? (
          <Card padded>
            <div data-templates>
              <SectionHeader
                title={matchedIndustry ? `Recommended for ${prettyIndustry(matchedIndustry)}` : 'Recommended templates'}
                subtitle="Tailored to your business. Install adds the object types — you can edit fields after."
              />
              <div className="grid gap-3 sm:grid-cols-2">
                {recommended.map(renderTemplateCard)}
              </div>
            </div>
          </Card>
        ) : (
          <Card padded>
            <div data-templates>
              <SectionHeader
                title="No recommended templates yet"
                subtitle={
                  matchedIndustry
                    ? `We don't have a template tailored to "${prettyIndustry(matchedIndustry)}" yet — browse other industries below or build a custom type from scratch.`
                    : 'Set your industry in Business Profile to get tailored recommendations, or browse all templates below.'
                }
                action={
                  !matchedIndustry && (
                    <Link to="/settings/business-profile" className="text-xs font-semibold text-primary hover:underline">
                      Set industry →
                    </Link>
                  )
                }
              />
            </div>
          </Card>
        )}

        {/* Other industries — collapsed by default */}
        {!loading && other.length > 0 && (
          <Card padded>
            <button
              type="button"
              onClick={() => setShowOther((v) => !v)}
              className="flex w-full items-center justify-between text-left"
            >
              <div>
                <div className="font-display text-sm font-semibold text-text-primary">Other industries</div>
                <div className="mt-0.5 text-xs text-text-muted">
                  Templates from {other.length} other vertical{other.length === 1 ? '' : 's'} — install if useful.
                </div>
              </div>
              {showOther ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
            </button>

            {showOther && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {other.map(renderTemplateCard)}
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
